// Typed wrapper + Zod schema for GET /api/runs/{id}/waveform_live, the
// progressive live-waveform cursor the backend serves while a run is being
// integrated. Contract is documented in damperlib/api_live.py: the endpoint
// always returns HTTP 200 and one of two shapes, discriminated on `available`.
//
// Advisory data only. When the payload reports available:false or a terminal
// status, the caller stops polling and the completed-run overlay (served from
// waveform_json) becomes the record of truth. Live samples are never folded
// into the completed-run chart.
import { z } from "zod";

// Live-stream status. This is the JobRunner/live-buffer lifecycle, NOT the DB
// run status (pending|running|complete|failed|cancelled). waiting|streaming are
// non-terminal; success|failed|timeout|cancelled are terminal.
export const LiveWaveformStatusSchema = z.enum([
  "waiting",
  "streaming",
  "success",
  "failed",
  "timeout",
  "cancelled",
]);
export type LiveWaveformStatus = z.infer<typeof LiveWaveformStatusSchema>;

const TERMINAL_LIVE_STATUSES: readonly LiveWaveformStatus[] = [
  "success",
  "failed",
  "timeout",
  "cancelled",
];

export function isLiveStatusTerminal(status: LiveWaveformStatus): boolean {
  return TERMINAL_LIVE_STATUSES.includes(status);
}

// Per-corner damper-position sample windows, metres, corner order LF/RF/LR/RR.
// Each corner defaults to an empty array: before the buffer binds its time
// grid, snapshot() returns channels:{} (the "waiting" branch in
// live_run_buffer.py), and each key is absent rather than an empty list.
const LiveChannelsSchema = z.object({
  damper_pos_lf: z.array(z.number()).optional().default([]),
  damper_pos_rf: z.array(z.number()).optional().default([]),
  damper_pos_lr: z.array(z.number()).optional().default([]),
  damper_pos_rr: z.array(z.number()).optional().default([]),
});

// Prefix R-squared vs the recorded truth, per corner. Any corner can be null
// (too few samples yet); the whole object is null on non-7-post studies.
const LiveR2Schema = z.object({
  lf: z.number().nullable(),
  rf: z.number().nullable(),
  lr: z.number().nullable(),
  rr: z.number().nullable(),
});
export type LiveR2 = z.infer<typeof LiveR2Schema>;

// Recorded ghost trace. Present only on the since=0 response for 7-post
// studies (the backend sends the full recorded truth once, then omits it).
const LiveRecordedSchema = z.object({
  time_s: z.array(z.number()),
  damper_pos_lf: z.array(z.number()),
  damper_pos_rf: z.array(z.number()),
  damper_pos_lr: z.array(z.number()),
  damper_pos_rr: z.array(z.number()),
});

export const LiveWaveformCursorSchema = z.object({
  available: z.literal(true),
  status: LiveWaveformStatusSchema,
  duration_s: z.number().nullable(),
  sim_time_s: z.number(),
  decimation_stride: z.number().int(),
  since: z.number().int(),
  next_since: z.number().int(),
  time_s: z.array(z.number()),
  channels: LiveChannelsSchema,
  live_r2_dyn: LiveR2Schema.nullable(),
  recorded: LiveRecordedSchema.nullable().optional(),
});
export type LiveWaveformCursor = z.infer<typeof LiveWaveformCursorSchema>;

export const LiveWaveformUnavailableSchema = z.object({
  available: z.literal(false),
  reason: z.string(),
  run_status: z.string().nullable(),
});
export type LiveWaveformUnavailable = z.infer<
  typeof LiveWaveformUnavailableSchema
>;

export const LiveWaveformResponseSchema = z.discriminatedUnion("available", [
  LiveWaveformCursorSchema,
  LiveWaveformUnavailableSchema,
]);
export type LiveWaveformResponse = z.infer<typeof LiveWaveformResponseSchema>;

export async function fetchLiveWaveform(
  runId: number,
  since: number,
  opts: { signal?: AbortSignal } = {},
): Promise<LiveWaveformResponse> {
  const r = await fetch(`/api/runs/${runId}/waveform_live?since=${since}`, {
    signal: opts.signal,
  });
  if (!r.ok) {
    throw new Error(`GET /api/runs/${runId}/waveform_live failed: ${r.status}`);
  }
  return LiveWaveformResponseSchema.parse(await r.json());
}

// ---------------------------------------------------------------------------
// Accumulator
// ---------------------------------------------------------------------------
// Folds successive cursor payloads into growing arrays. Samples are converted
// metres -> millimetres on ingest (×1000), consistent with overlayAdapter /
// multiRunOverlayAdapter so every damper-pos chart is in mm. The recorded
// ghost is captured once, from the first cursor that carries it.

const M_TO_MM = 1000;

export type LiveChannelArrays = {
  lf: number[];
  rf: number[];
  lr: number[];
  rr: number[];
};

export type LiveGhost = {
  timeS: number[]; // seconds
  channels: LiveChannelArrays; // mm
};

function toMm(arr: number[]): number[] {
  return arr.map((v) => v * M_TO_MM);
}

export class LiveWaveformAccumulator {
  runId: number | null = null;
  timeS: number[] = [];
  channels: LiveChannelArrays = { lf: [], rf: [], lr: [], rr: [] };
  ghost: LiveGhost | null = null;
  status: LiveWaveformStatus | null = null;
  durationS: number | null = null;
  simTimeS = 0;
  liveR2: LiveR2 | null = null;
  // Cursor to send on the next poll (the last payload's next_since).
  nextSince = 0;

  // Clear everything for a fresh run. Called on run change so a new run never
  // inherits the previous run's samples, ghost or cursor.
  reset(runId: number): void {
    this.runId = runId;
    this.timeS = [];
    this.channels = { lf: [], rf: [], lr: [], rr: [] };
    this.ghost = null;
    this.status = null;
    this.durationS = null;
    this.simTimeS = 0;
    this.liveR2 = null;
    this.nextSince = 0;
  }

  ingest(p: LiveWaveformCursor): void {
    // Metadata always reflects the latest cursor.
    this.status = p.status;
    this.durationS = p.duration_s;
    this.simTimeS = p.sim_time_s;
    this.liveR2 = p.live_r2_dyn;
    this.nextSince = p.next_since;

    // Ghost is captured once, from the first cursor that carries `recorded`.
    if (this.ghost === null && p.recorded) {
      this.ghost = {
        timeS: p.recorded.time_s.slice(),
        channels: {
          lf: toMm(p.recorded.damper_pos_lf),
          rf: toMm(p.recorded.damper_pos_rf),
          lr: toMm(p.recorded.damper_pos_lr),
          rr: toMm(p.recorded.damper_pos_rr),
        },
      };
    }

    // Fold the new-sample window. Skip any leading samples we already hold, so
    // a re-sent or overlapping cursor never double-appends (no-overlap).
    const have = this.timeS.length;
    const skip = have > p.since ? have - p.since : 0;
    const t = p.time_s;
    for (let i = skip; i < t.length; i++) {
      this.timeS.push(t[i]);
      this.channels.lf.push(p.channels.damper_pos_lf[i] * M_TO_MM);
      this.channels.rf.push(p.channels.damper_pos_rf[i] * M_TO_MM);
      this.channels.lr.push(p.channels.damper_pos_lr[i] * M_TO_MM);
      this.channels.rr.push(p.channels.damper_pos_rr[i] * M_TO_MM);
    }
  }
}
